import "@mdi/font/css/materialdesignicons.css";
import "@fontsource-variable/ibm-plex-sans";
import "@fontsource-variable/jetbrains-mono";
import "@/styles/vuetify-settings.scss";
import "@/styles/main.scss";
import { createVuetify, type ThemeDefinition } from "vuetify";
import { aliases, mdi } from "vuetify/iconsets/mdi";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import { ThemeOptions } from "@/api/preferences";

// Procyon brand palettes: blue-biased "nocturnal" neutrals, one interactive
// blue, plus two product-semantic colors that generic success/warning can't
// cover - "freeze" (glacial cyan, code freezes) and "release" (eyeshine gold,
// shipped versions). All pairs meet WCAG AA against their on-colors.
const procyonLightTheme: ThemeDefinition = {
    dark: false,
    colors: {
        // Background is kept distinctly greyer than the white surface so cards,
        // the app bar and the nav drawer read as elevated in light mode.
        background: "#eef1f6",
        "on-background": "#171a21",
        surface: "#ffffff",
        "on-surface": "#171a21",
        primary: "#2b59c3",
        "on-primary": "#ffffff",
        secondary: "#49536b",
        "on-secondary": "#ffffff",
        // Semantic colors are tuned to meet WCAG AA against their on-colors
        // (the original bright values failed contrast on a white surface).
        error: "#ba1a1a",
        "on-error": "#ffffff",
        success: "#1b7d33",
        "on-success": "#ffffff",
        info: "#00629e",
        "on-info": "#ffffff",
        warning: "#9a5400",
        "on-warning": "#ffffff",
        freeze: "#0e7c9c",
        "on-freeze": "#ffffff",
        release: "#f0b429",
        "on-release": "#2e2000",
    },
};

const procyonDarkTheme: ThemeDefinition = {
    dark: true,
    colors: {
        background: "#0c111e",
        "on-background": "#e7ebf5",
        surface: "#141b2c",
        "on-surface": "#e7ebf5",
        primary: "#93a9f7",
        "on-primary": "#10172a",
        secondary: "#9aa5c0",
        "on-secondary": "#10172a",
        error: "#ff6b6b",
        "on-error": "#10172a",
        success: "#6fcf8b",
        "on-success": "#10172a",
        info: "#79b8f0",
        "on-info": "#10172a",
        warning: "#f2a93b",
        "on-warning": "#10172a",
        freeze: "#5bc6e8",
        "on-freeze": "#10172a",
        release: "#f3c74b",
        "on-release": "#10172a",
    },
};

const diphyllaTheme: ThemeDefinition = {
    dark: true,
    colors: {
        background: "#282A36",
        "on-background": "#F8F8F2",
        surface: "#44475A",
        "on-surface": "#F8F8F2",
        primary: "#BD93F9",
        "on-primary": "#282A36",
        secondary: "#8F9DC9",
        "on-secondary": "#F8F8F2",
        error: "#FF5555",
        "on-error": "#F8F8F2",
        success: "#50FA7B",
        "on-success": "#282A36",
        info: "#FF79C6",
        "on-info": "#F8F8F2",
        warning: "#FFB86C",
        "on-warning": "#F8F8F2",
        freeze: "#8BE9FD",
        "on-freeze": "#282A36",
        release: "#F1FA8C",
        "on-release": "#282A36",
    },
};

export type ProcyonTheme = {
    name: string;
    themeOption: ThemeOptions;
    value: string | null;
};

export const procyonThemes: ProcyonTheme[] = [
    { name: "Follow System Theme", themeOption: ThemeOptions.Default, value: null },
    {
        name: "Procyon Light",
        themeOption: ThemeOptions.DefaultLight,
        value: "procyonLightTheme",
    },
    { name: "Procyon Dark", themeOption: ThemeOptions.DefaultDark, value: "procyonDarkTheme" },
    { name: "Diphylla", themeOption: ThemeOptions.Diphylla, value: "diphyllaTheme" },
];

export default createVuetify({
    components,
    directives,
    theme: {
        defaultTheme: "procyonLightTheme",
        themes: {
            procyonLightTheme,
            procyonDarkTheme,
            diphyllaTheme,
        },
        variations: {
            colors: [
                "primary",
                "surface",
                "secondary",
                "error",
                "success",
                "info",
                "warning",
                "freeze",
                "release",
            ],
            lighten: 2,
            darken: 2,
        },
    },
    defaults: {
        VBtn: {
            color: "primary",
        },
        VCard: {
            VCardActions: {
                VBtn: { variant: "elevated" },
            },
        },
        VCheckbox: {
            color: "primary",
        },
        // Vuetify 4 leaves the browser's own autofill on, which drops remembered entries over
        // the item menu.
        VAutocomplete: {
            autocomplete: "off",
        },
        VCombobox: {
            color: "primary",
            autocomplete: "off",
        },
        VSwitch: {
            color: "primary",
        },
        VTextField: {
            color: "primary",
        },
    },
    icons: {
        defaultSet: "mdi",
        aliases,
        sets: {
            mdi,
        },
    },
});
